import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import './Login.css';

// Mock users for testing
const MOCK_USERS = [
  {
    id: 1,
    employeeCode: 'NV001',
    name: 'Nguyễn Văn A',
    username: 'Nguyễn Văn A',
    email: 'admin@ey.com',
    phone: '0901234567',
    password: 'admin123',
    role: 'Admin',
    roleIds: [1, 2, 3],
    department: 'Kiểm toán',
    managedBranches: ['all'],
    positions: [1, 2, 3],
    status: 'Hoạt động'
  },
  {
    id: 2,
    employeeCode: 'NV002',
    name: 'Trần Thị B',
    username: 'Trần Thị B',
    email: 'manager@ey.com',
    phone: '0901234568',
    password: 'manager123',
    role: 'Manager',
    roleIds: [2, 3],
    department: 'Tín dụng',
    managedBranches: ['CN TP HCM', 'CN Đà Nẵng'],
    positions: [2, 3],
    status: 'Hoạt động'
  },
  {
    id: 3,
    employeeCode: 'NV003',
    name: 'Lê Văn C',
    username: 'Lê Văn C',
    email: 'analyst@ey.com',
    phone: '0901234569',
    password: 'analyst123',
    role: 'Analyst',
    roleIds: [3],
    department: 'Phân tích',
    managedBranches: ['CN Hà Nội'],
    positions: [3],
    status: 'Hoạt động'
  }
];

function Login() {
  const navigate = useNavigate();
  const { login, logout, currentUser } = useAuth();
  const { t } = useTranslation();
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showLogoutPrompt, setShowLogoutPrompt] = useState(false);

  useEffect(() => {
    // If already logged in, show logout prompt instead of redirect
    if (currentUser) {
      setShowLogoutPrompt(true);
    }
  }, [currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Simulate API call delay
    setTimeout(() => {
      // Find user by email or phone
      const user = MOCK_USERS.find(
        u => (u.email === loginId || u.phone === loginId) && u.password === password
      );

      if (user) {
        // Remove password before storing
        const { password: _, ...userWithoutPassword } = user;
        login(userWithoutPassword);
        navigate('/');
      } else {
        setError(t('auth.loginError'));
      }
      setLoading(false);
    }, 800);
  };

  return (
    <div className="login-container">
      {/* Animated Background */}
      <div className="login-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        <div className="floating-shape shape-5"></div>
      </div>

      {/* Login Card */}
      <div className="login-card">
        {/* EY Logo & Brand */}
        <div className="login-header">
          <div className="ey-logo">
            <div className="ey-box">
              <span className="ey-text">EY</span>
            </div>
          </div>
          <h1 className="login-title">
            Early Warning System
          </h1>
          <p className="login-subtitle">
            {t('dashboard.subtitle')}
          </p>
        </div>

        {/* Already Logged In Prompt */}
        {showLogoutPrompt && (
          <div className="alert alert-info fade-in mb-4" role="alert">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <i className="bi bi-info-circle me-2"></i>
                <strong>{t('auth.alreadyLoggedIn') || 'Bạn đã đăng nhập'}</strong>
                <div className="mt-2">
                  {t('auth.currentUser') || 'Người dùng'}: <strong>{currentUser?.email}</strong>
                </div>
              </div>
              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => navigate('/')}
                >
                  <i className="bi bi-house me-1"></i>
                  {t('auth.goToDashboard') || 'Về Trang chủ'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-warning"
                  onClick={() => {
                    logout();
                    setShowLogoutPrompt(false);
                  }}
                >
                  <i className="bi bi-box-arrow-right me-1"></i>
                  {t('auth.logout')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="login-form" style={{ opacity: showLogoutPrompt ? 0.5 : 1 }}>
          {error && (
            <div className="alert alert-danger fade-in" role="alert">
              <i className="bi bi-exclamation-circle me-2"></i>
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="loginId">
              <i className="bi bi-person-circle me-2"></i>
              {t('auth.email')}
            </label>
            <input
              type="text"
              id="loginId"
              className="form-control"
              placeholder={t('auth.email')}
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">
              <i className="bi bi-lock me-2"></i>
              {t('auth.password')}
            </label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="form-control"
                placeholder={t('auth.password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                <i className={`bi bi-eye${showPassword ? '-slash' : ''}`}></i>
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-login"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {t('common.loading')}
              </>
            ) : (
              <>
                <i className="bi bi-box-arrow-in-right me-2"></i>
                {t('auth.loginButton')}
              </>
            )}
          </button>
        </form>

        {/* Footer */}
        <div className="login-footer">
          <p className="text-muted">
            Powered by <strong>EY Vietnam</strong>
          </p>
          <p className="text-muted small">
            © 2026 Ernst & Young. All Rights Reserved.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
